import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import Header from "../../shef_dashboard/components/Header";
import { handleChangeOrderStatus, handleGetOrders } from "../../services/order";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import moment from "moment";
import { handleGetDefaultSetting } from "../../services/default_setting";
import OrderList from "../components/order/OrderList";
import Footer from "../../frontend/components/Footer";

export const Order = () => {
  const { authToken } = useSelector((state) => state.user);
  const { userInfo } = useSelector((state) => state.user);
  const location = useLocation();

  const [defaultSettings, setDefaultSettings] = useState([]);
  const [orderDetails, setOrderDetails] = useState([]);
  const [isFetching, setIsFetching] = useState(false);
  const [allOrders, setAllOrders] = useState([]);
  const [filterSearch, setFilterSearch] = useState("");
  const [filterDate, setFilterDate] = useState("");
  const [refetchOrder, setRefetchOrder] = useState(false);

  // Cancel confirmation modal state
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancellingOrder, setCancellingOrder] = useState(null); // { id, newStatus }
  const [cancelReason, setCancelReason] = useState("");

  const [currentTab, setCurrentTab] = useState(location.state?.activeTab || "all_orders");
  const [orderListToPass, setOrderListToPass] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  const [filterBy, setFilterBy] = useState("delivery_date_time");
  const [groupBy, setGroupBy] = useState("delivery_address");

  useEffect(() => {
    const fetchOrders = async () => {
      try {
        setIsFetching(true);
        const ordersRetrieved = await handleGetOrders(authToken, {
          chef_id: userInfo.id,
        });

        
        const aggregatedOrders = ordersRetrieved.map((order) => {
          // Group order_details by user_menu_id (or name if id not available)
          const groupedItems = {};
          order.order_details.forEach((detail) => {
            const key = detail.user_menu_id || detail.name; // fallback to name
            if (!groupedItems[key]) {
              groupedItems[key] = {
                name: detail.name,
                quantity: 0,
                unit_price: detail.unit_price,
              };
            }
            groupedItems[key].quantity += detail.quantity;
          });

          // Create a human‑readable summary string
          const dishSummary = Object.values(groupedItems)
            .map((item) => `${item.name} x${item.quantity}`)
            .join(", ");

          // Total quantity for the whole order
          const totalQuantity = Object.values(groupedItems).reduce(
            (sum, item) => sum + item.quantity,
            0
          );

          return {
            ...order, // keep all original fields (delivery_address, total_price, etc.)
            dish_summary: dishSummary,
            total_quantity: totalQuantity,
            // Optionally also expose grouped items if needed elsewhere
            grouped_items: Object.values(groupedItems),
          };
        });

        setAllOrders(aggregatedOrders || []);
      } catch (error) {
        console.log("Error While Fetching Orders \n", error);
        setAllOrders([]);
      } finally {
        setIsFetching(false);
      }
    };

    fetchOrders();
  }, [authToken, refetchOrder, userInfo.id]);

  const indexOfLastOrder = currentPage * itemsPerPage;
  const indexOfFirstOrder = indexOfLastOrder - itemsPerPage;
  const currentOrders = orderListToPass?.slice(indexOfFirstOrder, indexOfLastOrder);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);
  const totalPages = Math.ceil(orderListToPass?.length / itemsPerPage);

  useEffect(() => {
    const getDefaultSettings = async () => {
      try {
        setIsFetching(true);
        const retrieveDefaultSettings = await handleGetDefaultSetting(authToken);
        setDefaultSettings(retrieveDefaultSettings);
      } catch (error) {
        console.log("Error While Fetching Order Settings \n", error);
      } finally {
        setIsFetching(false);
      }
    };
    getDefaultSettings();
  }, [authToken]);

  const handleOnChange = (e) => {
    const searchTerm = e.target.value.toLowerCase();
    setFilterSearch(searchTerm);
  };

  const handleDateChange = (e) => {
    setFilterDate(e.target.value);
  };

  const extractAreaFromAddress = (fullAddress) => {
    if (!fullAddress || typeof fullAddress !== "string") return "Unknown Area";

    const addressParts = fullAddress.toLowerCase().split(/[,\s]+/);
    const areaKeywords = ["area", "block", "sector", "phase", "town", "colony", "nagar"];

    let areaFound = "";
    for (let i = 0; i < addressParts.length; i++) {
      if (areaKeywords.some((keyword) => addressParts[i].includes(keyword))) {
        const prevPart = i > 0 ? addressParts[i - 1] : "";
        areaFound = `${prevPart} ${addressParts[i]}`.trim();
        const nextPart = i < addressParts.length - 1 ? addressParts[i + 1] : "";
        if (!isNaN(nextPart)) {
          areaFound = `${areaFound} ${nextPart}`;
        }
        break;
      }
    }

    if (!areaFound) {
      areaFound = addressParts.slice(0, 3).join(" ");
    }

    return areaFound.charAt(0).toUpperCase() + areaFound.slice(1);
  };

  const sortOrders = (orders, sortBy) => {
    const bottomStatuses = new Set(["delivered", "delivering", "canceled"]);
    const sortedOrders = [...orders];

    sortedOrders.sort((a, b) => {
      const aIsBottom = bottomStatuses.has(a.status);
      const bIsBottom = bottomStatuses.has(b.status);

      if (aIsBottom && !bIsBottom) return 1;
      if (!aIsBottom && bIsBottom) return -1;

      switch (sortBy) {
        case "delivery_date_time": {
          const getOrderDateTime = (order) => {
            let dateTime;
            if (order.delivery_date && order.delivery_time) {
              dateTime = moment(
                `${order.delivery_date} ${order.delivery_time}`,
                "YYYY-MM-DD HH:mm:ss"
              );
            } else if (order.delivery_date_time) {
              dateTime = moment(order.delivery_date_time);
            } else {
              return moment.invalid();
            }
            return dateTime;
          };

          const dateTimeA = getOrderDateTime(a);
          const dateTimeB = getOrderDateTime(b);

          if (!dateTimeA.isValid()) return 1;
          if (!dateTimeB.isValid()) return -1;

          return dateTimeA.diff(dateTimeB);
        }
        case "created_at":
          return moment(b.created_at).diff(moment(a.created_at));
        case "total_price":
          return parseFloat(a.total_price || 0) - parseFloat(b.total_price || 0);
        case "customer_name": {
          const nameA = a.customer?.name || "";
          const nameB = b.customer?.name || "";
          return nameA.localeCompare(nameB);
        }
        default:
          return 0;
      }
    });

    return sortedOrders;
  };

  const groupOrders = (orders, groupByOption) => {
    if (groupByOption === "none") {
      return orders;
    }

    const grouped = orders.reduce((groups, order) => {
      let key;
      switch (groupByOption) {
        case "delivery_address":
          key = extractAreaFromAddress(order.delivery_address);
          break;
        case "status":
          key = order.status || "Unknown";
          break;
        case "delivery_date":
          key = order.delivery_date
            ? moment(order.delivery_date).format("LL")
            : "No Date";
          break;
        case "customer":
          key = order.customer?.name || "Unknown Customer";
          break;
        default:
          key = "all";
          break;
      }

      if (!groups[key]) {
        groups[key] = [];
      }
      groups[key].push(order);
      return groups;
    }, {});

    const sortedKeys = Object.keys(grouped).sort();
    return sortedKeys.flatMap((key) => grouped[key]);
  };

  function searchOrders(orders, searchTerm, filterDate) {
    if (!orders) return [];

    return orders.filter((order) => {
      const deliveryDate = order.delivery_date
        ? moment(order.delivery_date).format("YYYY-MM-DD")
        : order.delivery_date_time
          ? moment(order.delivery_date_time).format("YYYY-MM-DD")
          : null;

      const matchesDate = !filterDate || deliveryDate === filterDate;

      const lowercasedSearchTerm = searchTerm.toLowerCase();
      const matchesSearch =
        !lowercasedSearchTerm ||
        order.order_code?.toLowerCase().includes(lowercasedSearchTerm) ||
        order.dish_summary?.toLowerCase().includes(lowercasedSearchTerm); 
      return matchesDate && matchesSearch;
    });
  }

  useEffect(() => {
    let processedOrders = searchOrders(allOrders, filterSearch, filterDate);
    processedOrders = sortOrders(processedOrders, filterBy);
    processedOrders = groupOrders(processedOrders, groupBy);

    setOrderDetails(processedOrders);
    setCurrentPage(1);
  }, [allOrders, filterSearch, filterDate, filterBy, groupBy]);

  // Core function to update order status (calls API)
  const updateOrderStatus = async (id, status, reason = null) => {
    try {
      const payload = { status };
      if (reason) payload.cancellation_reason = reason;
      const ordersRetrieved = await handleChangeOrderStatus(
        authToken,
        payload,
        id
      );
      if (ordersRetrieved.success) {
        toast.success(ordersRetrieved.success || "Order Status Updated!");
        setRefetchOrder((prevState) => !prevState);
      }
    } catch (error) {
      console.log("Error While Changing Order's state \n", error);
      toast.warn(error.error || "Order Status Failed to Update!");
    }
  };

  const handleStatusChange = (e, id) => {
    const newStatus = e.target?.value?.toLowerCase();
    if (newStatus === "canceled") {
      setCancellingOrder({ id });
      setShowCancelModal(true);
    } else {
      updateOrderStatus(id, newStatus);
    }
  };

  const confirmCancel = async () => {
    if (!cancelReason.trim()) {
      toast.warn("Please provide a reason for cancellation.");
      return;
    }
    if (cancellingOrder) {
      await updateOrderStatus(cancellingOrder.id, 'canceled', cancelReason.trim());
      setShowCancelModal(false);
      setCancellingOrder(null);
      setCancelReason("");
    }
  };

  const closeCancelModal = () => {
    setShowCancelModal(false);
    setCancellingOrder(null);
    setCancelReason("");
  };

  useEffect(() => {
    if (currentTab === "all_orders") {
      setOrderListToPass(orderDetails);
    } else {
      const filteredOrders = orderDetails?.filter(
        (order) => order.status === currentTab
      );
      setOrderListToPass(filteredOrders);
    }
  }, [currentTab, orderDetails]);

  const getStatusCount = (status) => {
    return allOrders?.filter((order) => order.status === status)?.length;
  };

  return (
    <>
      <div className="bg-gradient-to-br from-gray-50 to-gray-100 min-h-screen">
        <Header />
        <div className="container mx-auto px-4 py-8 max-w-7xl">
          {/* Page Header */}
          <div className="mb-8">
            <h1 className="text-4xl font-extrabold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent mb-2">
              Orders Management
            </h1>
            <p className="text-lg text-gray-600">
              Track and manage all your customer orders in one place
            </p>
          </div>

          {/* Filters Section */}
          <div className="bg-white rounded-2xl shadow-xl p-8 mb-8 border border-gray-100">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-lg flex items-center justify-center text-white">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <circle cx="11" cy="11" r="8"></circle>
                  <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-gray-800">
                Filter & Search Orders
              </h2>
            </div>

            <div className="bg-gradient-to-r from-amber-50 to-amber-100 border border-amber-200 rounded-xl p-4 mb-6 flex items-center gap-2 text-amber-800 font-medium">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="12" y1="8" x2="12" y2="12"></line>
                <line x1="12" y1="16" x2="12.01" y2="16"></line>
              </svg>
              <span>
                Orders that fail to deliver by delivery date will automatically
                be cancelled.
              </span>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
              <div className="flex flex-col gap-2">
                <label className="text-sm font-semibold text-gray-700 uppercase tracking-wider">
                  Select Date
                </label>
                <input
                  type="date"
                  className="px-4 py-3 border-2 border-gray-200 rounded-xl bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                  value={filterDate}
                  onChange={handleDateChange}
                />
              </div>

              <div className="flex flex-col gap-2">
                <label className="text-sm font-semibold text-gray-700 uppercase tracking-wider">
                  Search Orders
                </label>
                <div className="relative">
                  <input
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all pr-10"
                    placeholder="Search by Name or Order ID"
                    disabled={allOrders?.length < 1}
                    value={filterSearch}
                    onChange={handleOnChange}
                  />
                  <div className="absolute right-3 top-3.5 text-gray-400">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <circle cx="11" cy="11" r="8"></circle>
                      <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                    </svg>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Status Filters */}
          <div className="flex flex-wrap gap-2 mb-6">
            <button
              className={`px-6 py-3 rounded-xl font-semibold text-sm uppercase tracking-wider transition-all ${currentTab === "all_orders"
                  ? "bg-gradient-to-r from-red-600 to-red-700 text-white shadow-lg"
                  : "bg-gray-50 text-gray-500 hover:bg-gray-100 border border-gray-200"
                }`}
              onClick={() => setCurrentTab("all_orders")}
            >
              All Orders{" "}
              <span className="bg-white bg-opacity-30 px-2 py-1 rounded-md ml-1">
                {orderDetails?.length}
              </span>
            </button>

            <button
              className={`px-6 py-3 rounded-xl font-semibold text-sm uppercase tracking-wider transition-all ${currentTab === "pending"
                  ? "bg-gradient-to-r from-amber-500 to-orange-500 text-white shadow-lg"
                  : "bg-gray-50 text-gray-500 hover:bg-gray-100 border border-gray-200"
                }`}
              onClick={() => setCurrentTab("pending")}
            >
              Pending Orders{" "}
              <span className="bg-white bg-opacity-30 px-2 py-1 rounded-md ml-1">
                {getStatusCount("pending")}
              </span>
            </button>

            <button
              className={`px-6 py-3 rounded-xl font-semibold text-sm uppercase tracking-wider transition-all ${currentTab === "accepted"
                  ? "bg-gradient-to-r from-emerald-500 to-green-600 text-white shadow-lg"
                  : "bg-gray-50 text-gray-500 hover:bg-gray-100 border border-gray-200"
                }`}
              onClick={() => setCurrentTab("accepted")}
            >
              Accepted Orders{" "}
              <span className="bg-white bg-opacity-30 px-2 py-1 rounded-md ml-1">
                {getStatusCount("accepted")}
              </span>
            </button>

            <button
              className={`px-6 py-3 rounded-xl font-semibold text-sm uppercase tracking-wider transition-all ${currentTab === "preparing"
                  ? "bg-gradient-to-r from-blue-500 to-blue-600 text-white shadow-lg"
                  : "bg-gray-50 text-gray-500 hover:bg-gray-100 border border-gray-200"
                }`}
              onClick={() => setCurrentTab("preparing")}
            >
              In Process{" "}
              <span className="bg-white bg-opacity-30 px-2 py-1 rounded-md ml-1">
                {getStatusCount("preparing")}
              </span>
            </button>

            <button
              className={`px-6 py-3 rounded-xl font-semibold text-sm uppercase tracking-wider transition-all ${currentTab === "delivering"
                  ? "bg-gradient-to-r from-purple-500 to-violet-600 text-white shadow-lg"
                  : "bg-gray-50 text-gray-500 hover:bg-gray-100 border border-gray-200"
                }`}
              onClick={() => setCurrentTab("delivering")}
            >
              Ready to Deliver{" "}
              <span className="bg-white bg-opacity-30 px-2 py-1 rounded-md ml-1">
                {getStatusCount("delivering")}
              </span>
            </button>

            <button
              className={`px-6 py-3 rounded-xl font-semibold text-sm uppercase tracking-wider transition-all ${currentTab === "delivered"
                  ? "bg-gradient-to-r from-cyan-500 to-teal-600 text-white shadow-lg"
                  : "bg-gray-50 text-gray-500 hover:bg-gray-100 border border-gray-200"
                }`}
              onClick={() => setCurrentTab("delivered")}
            >
              Delivered{" "}
              <span className="bg-white bg-opacity-30 px-2 py-1 rounded-md ml-1">
                {getStatusCount("delivered")}
              </span>
            </button>

            <button
              className={`px-6 py-3 rounded-xl font-semibold text-sm uppercase tracking-wider transition-all ${currentTab === "canceled"
                  ? "bg-gradient-to-r from-red-600 to-red-700 text-white shadow-lg"
                  : "bg-gray-50 text-gray-500 hover:bg-gray-100 border border-gray-200"
                }`}
              onClick={() => setCurrentTab("canceled")}
            >
              Cancelled{" "}
              <span className="bg-white bg-opacity-30 px-2 py-1 rounded-md ml-1">
                {getStatusCount("canceled")}
              </span>
            </button>
          </div>

          {/* Orders Table */}
          <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100 overflow-hidden">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-10 h-10 bg-gradient-to-r from-red-600 to-red-700 rounded-lg flex items-center justify-center text-white">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"></path>
                  <line x1="3" y1="6" x2="21" y2="6"></line>
                  <path d="M16 10a4 4 0 0 1-8 0"></path>
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-gray-800">Your Orders</h2>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-gradient-to-r from-gray-50 to-gray-100">
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider rounded-tl-xl">
                      Order ID
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      Dish Name
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      Order Date & Time
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      Delivery Date & Time
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      Delivery Address
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      Customer
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      Phone Number
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      Quantity
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      Shef Earning
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      Total Price
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider rounded-tr-xl">
                      Action
                    </th>
                  </tr>
                </thead>
                <OrderList
                  orderDetails={currentOrders}
                  handleStatusChange={handleStatusChange}
                  defaultSettings={defaultSettings}
                  isFetching={isFetching}
                />
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex justify-center mt-8">
                <div className="flex gap-2">
                  {Array.from({ length: totalPages }, (_, index) => index + 1).map(
                    (pageNumber) => (
                      <button
                        key={pageNumber}
                        onClick={() => paginate(pageNumber)}
                        className={`w-10 h-10 rounded-lg flex items-center justify-center font-semibold transition-all ${pageNumber === currentPage
                            ? "bg-gradient-to-r from-red-600 to-red-700 text-white shadow-md"
                            : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                          }`}
                      >
                        {pageNumber}
                      </button>
                    )
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Cancel Confirmation Modal */}
          {showCancelModal && (
            <div
              className="fixed inset-0 z-50 overflow-y-auto"
              aria-labelledby="modal-title"
              role="dialog"
              aria-modal="true"
            >
              <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
                <div
                  className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
                  aria-hidden="true"
                  onClick={closeCancelModal}
                ></div>

                <span
                  className="hidden sm:inline-block sm:align-middle sm:h-screen"
                  aria-hidden="true"
                >
                  &#8203;
                </span>
                <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
                  <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                    <div className="sm:flex sm:items-start">
                      <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                        <svg
                          className="h-6 w-6 text-red-600"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          aria-hidden="true"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                          />
                        </svg>
                      </div>
                      <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                        <h3
                          className="text-lg leading-6 font-medium text-gray-900"
                          id="modal-title"
                        >
                          Cancel Order
                        </h3>
                        <div className="mt-2">
                          <p className="text-sm text-gray-500">
                            Are you sure you want to cancel this order? This
                            action cannot be undone.
                          </p>
                          <textarea
                            className="mt-3 w-full border rounded-md p-2 text-sm text-gray-700"
                            rows="3"
                            placeholder="Please provide a reason for cancellation (required)"
                            value={cancelReason}
                            onChange={(e) => setCancelReason(e.target.value)}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                    <button
                      type="button"
                      onClick={confirmCancel}
                      className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm"
                    >
                      Yes, Cancel Order
                    </button>
                    <button
                      type="button"
                      onClick={closeCancelModal}
                      className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                    >
                      No, Keep Order
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      <Footer />
    </>
  );
};

export default Order;